// src/components/RestaurantSetup.js
import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { tenantService } from "../services/tenantService";
import { documentProcessor } from "../services/documentProcessingService";
import PropTypes from "prop-types";
import "./RestaurantSetup.css";

const RestaurantSetup = ({ onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form data
  const [restaurantInfo, setRestaurantInfo] = useState({
    name: "",
    description: "",
    address: "",
    phone: "",
    email: "",
    website: "",
  });

  const [theme, setTheme] = useState({
    primaryColor: "#d4af37",
    secondaryColor: "#31231f",
    backgroundColor: "#f8f4e3",
    textColor: "#31231f",
    fontFamily: "Arial, sans-serif",
    titleFont: "Georgia, serif",
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [processedMenus, setProcessedMenus] = useState([]);
  const [processingStatus, setProcessingStatus] = useState("");

  // Document upload handler
  const onDrop = useCallback(async (acceptedFiles) => {
    setProcessingStatus("Processing documents...");
    const results = [];

    for (const file of acceptedFiles) {
      try {
        let processedData;

        if (file.type === "application/pdf") {
          processedData = await documentProcessor.processPDF(file);
        } else if (
          file.type.includes("word") ||
          file.name.endsWith(".docx") ||
          file.name.endsWith(".doc")
        ) {
          processedData = await documentProcessor.processWordDocument(file);
        } else {
          throw new Error(
            "Unsupported file type. Please upload PDF or Word documents."
          );
        }

        const cleanedData = documentProcessor.validateAndClean(processedData);

        results.push({
          fileName: file.name,
          fileSize: file.size,
          processedAt: new Date(),
          ...cleanedData,
        });
      } catch (err) {
        console.error(`Error processing ${file.name}:`, err);
        setError(`Failed to process ${file.name}: ${err.message}`);
      }
    }

    setUploadedDocuments((prev) => [...prev, ...acceptedFiles]);
    setProcessedMenus((prev) => [...prev, ...results]);
    setProcessingStatus("");
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    multiple: true,
  });

  // Handle form submission
  const handleSubmit = async () => {
    if (step < 4) {
      setStep(step + 1);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Create restaurant
      const restaurant = await tenantService.createRestaurant({
        ...restaurantInfo,
        theme,
      });

      // If there are processed menus, we would import them here
      // For now, we'll just complete the setup

      if (onComplete) {
        onComplete(restaurant);
      } else {
        navigate(`/restaurant/${restaurant.slug}/admin`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="setup-step">
            <h2>Restaurant Information</h2>
            <p>
              Let's start with some basic information about your restaurant.
            </p>

            <div className="form-group">
              <label htmlFor="name">Restaurant Name *</label>
              <input
                id="name"
                type="text"
                value={restaurantInfo.name}
                onChange={(e) =>
                  setRestaurantInfo({ ...restaurantInfo, name: e.target.value })
                }
                placeholder="Lucky Dog Tavern"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={restaurantInfo.description}
                onChange={(e) =>
                  setRestaurantInfo({
                    ...restaurantInfo,
                    description: e.target.value,
                  })
                }
                placeholder="A cozy neighborhood tavern with great food and wine"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Address</label>
              <input
                id="address"
                type="text"
                value={restaurantInfo.address}
                onChange={(e) =>
                  setRestaurantInfo({
                    ...restaurantInfo,
                    address: e.target.value,
                  })
                }
                placeholder="123 Main St, Anytown, ST 12345"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  type="tel"
                  value={restaurantInfo.phone}
                  onChange={(e) =>
                    setRestaurantInfo({
                      ...restaurantInfo,
                      phone: e.target.value,
                    })
                  }
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={restaurantInfo.email}
                  onChange={(e) =>
                    setRestaurantInfo({
                      ...restaurantInfo,
                      email: e.target.value,
                    })
                  }
                  placeholder="info@yourrestaurant.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                type="url"
                value={restaurantInfo.website}
                onChange={(e) =>
                  setRestaurantInfo({
                    ...restaurantInfo,
                    website: e.target.value,
                  })
                }
                placeholder="https://yourrestaurant.com"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="setup-step">
            <h2>Customize Your Brand</h2>
            <p>Choose colors and fonts that match your restaurant's style.</p>

            <div className="theme-customization">
              <div className="color-section">
                <h3>Colors</h3>
                <div className="color-inputs">
                  <div className="color-input-group">
                    <label htmlFor="primaryColor">Primary Color</label>
                    <input
                      id="primaryColor"
                      type="color"
                      value={theme.primaryColor}
                      onChange={(e) =>
                        setTheme({ ...theme, primaryColor: e.target.value })
                      }
                    />
                    <span>{theme.primaryColor}</span>
                  </div>

                  <div className="color-input-group">
                    <label htmlFor="secondaryColor">Secondary Color</label>
                    <input
                      id="secondaryColor"
                      type="color"
                      value={theme.secondaryColor}
                      onChange={(e) =>
                        setTheme({ ...theme, secondaryColor: e.target.value })
                      }
                    />
                    <span>{theme.secondaryColor}</span>
                  </div>

                  <div className="color-input-group">
                    <label htmlFor="backgroundColor">Background Color</label>
                    <input
                      id="backgroundColor"
                      type="color"
                      value={theme.backgroundColor}
                      onChange={(e) =>
                        setTheme({ ...theme, backgroundColor: e.target.value })
                      }
                    />
                    <span>{theme.backgroundColor}</span>
                  </div>
                </div>
              </div>

              <div className="font-section">
                <h3>Typography</h3>
                <div className="font-inputs">
                  <div className="form-group">
                    <label htmlFor="titleFont">Title Font</label>
                    <select
                      id="titleFont"
                      value={theme.titleFont}
                      onChange={(e) =>
                        setTheme({ ...theme, titleFont: e.target.value })
                      }
                    >
                      <option value="Georgia, serif">Georgia (Serif)</option>
                      <option value="'Times New Roman', serif">
                        Times New Roman (Serif)
                      </option>
                      <option value="'Playfair Display', serif">
                        Playfair Display (Serif)
                      </option>
                      <option value="Arial, sans-serif">
                        Arial (Sans-serif)
                      </option>
                      <option value="'Helvetica Neue', sans-serif">
                        Helvetica Neue (Sans-serif)
                      </option>
                      <option value="'Open Sans', sans-serif">
                        Open Sans (Sans-serif)
                      </option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="fontFamily">Body Font</label>
                    <select
                      id="fontFamily"
                      value={theme.fontFamily}
                      onChange={(e) =>
                        setTheme({ ...theme, fontFamily: e.target.value })
                      }
                    >
                      <option value="Arial, sans-serif">
                        Arial (Sans-serif)
                      </option>
                      <option value="'Helvetica Neue', sans-serif">
                        Helvetica Neue (Sans-serif)
                      </option>
                      <option value="'Open Sans', sans-serif">
                        Open Sans (Sans-serif)
                      </option>
                      <option value="Georgia, serif">Georgia (Serif)</option>
                      <option value="'Times New Roman', serif">
                        Times New Roman (Serif)
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="preview-section">
                <h3>Preview</h3>
                <div
                  className="theme-preview"
                  style={{
                    backgroundColor: theme.backgroundColor,
                    color: theme.textColor,
                    fontFamily: theme.fontFamily,
                    padding: "20px",
                    borderRadius: "8px",
                    border: `2px solid ${theme.primaryColor}`,
                  }}
                >
                  <h1
                    style={{
                      fontFamily: theme.titleFont,
                      color: theme.primaryColor,
                      margin: "0 0 10px 0",
                    }}
                  >
                    {restaurantInfo.name || "Your Restaurant Name"}
                  </h1>
                  <p style={{ margin: "0 0 15px 0" }}>
                    {restaurantInfo.description ||
                      "Your restaurant description will appear here."}
                  </p>
                  <div
                    style={{
                      backgroundColor: theme.primaryColor,
                      color: theme.backgroundColor,
                      padding: "8px 16px",
                      borderRadius: "4px",
                      display: "inline-block",
                    }}
                  >
                    Sample Menu Item - $12.99
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="setup-step">
            <h2>Upload Your Menu</h2>
            <p>
              Upload your existing menu documents (PDF or Word format) and we'll
              help you digitize them.
            </p>

            <div className="document-upload">
              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? "active" : ""}`}
              >
                <input {...getInputProps()} />
                <div className="dropzone-content">
                  <div className="upload-icon">📄</div>
                  {isDragActive ? (
                    <p>Drop your menu documents here...</p>
                  ) : (
                    <div>
                      <p>
                        <strong>Drag & drop</strong> your menu files here, or{" "}
                        <strong>click to browse</strong>
                      </p>
                      <p className="file-types">Supports: PDF, DOC, DOCX</p>
                    </div>
                  )}
                </div>
              </div>

              {processingStatus && (
                <div className="processing-status">
                  <div className="spinner"></div>
                  <span>{processingStatus}</span>
                </div>
              )}

              {uploadedDocuments.length > 0 && (
                <div className="uploaded-files">
                  <h3>Uploaded Documents</h3>
                  {uploadedDocuments.map((file) => (
                    <div
                      key={`${file.name}-${file.size}`}
                      className="file-item"
                    >
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {processedMenus.length > 0 && (
                <div className="processed-results">
                  <h3>Processing Results</h3>
                  {processedMenus.map((menu) => (
                    <div
                      key={`${menu.fileName}-${menu.processedAt.getTime()}`}
                      className="menu-result"
                    >
                      <h4>{menu.fileName}</h4>
                      <div className="stats">
                        <span>{menu.statistics.totalItems} items found</span>
                        <span>
                          {menu.statistics.categoriesFound} categories
                        </span>
                        <span>
                          {menu.statistics.confidencePercentage}% confidence
                        </span>
                      </div>
                      {menu.suggestions.length > 0 && (
                        <div className="suggestions">
                          {menu.suggestions.map((suggestion) => (
                            <div
                              key={`${suggestion.type}-${suggestion.message}`}
                              className={`suggestion ${suggestion.type}`}
                            >
                              {suggestion.message}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="skip-option">
              <p>
                Don't have digital menus?{" "}
                <button type="button" className="link-button">
                  Skip this step
                </button>{" "}
                and add items manually later.
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="setup-step">
            <h2>Review & Launch</h2>
            <p>Review your restaurant setup and launch your digital menu!</p>

            <div className="setup-summary">
              <div className="summary-section">
                <h3>Restaurant Information</h3>
                <div className="summary-item">
                  <strong>Name:</strong> {restaurantInfo.name}
                </div>
                {restaurantInfo.description && (
                  <div className="summary-item">
                    <strong>Description:</strong> {restaurantInfo.description}
                  </div>
                )}
                {restaurantInfo.address && (
                  <div className="summary-item">
                    <strong>Address:</strong> {restaurantInfo.address}
                  </div>
                )}
              </div>

              <div className="summary-section">
                <h3>Menu Items</h3>
                {processedMenus.length > 0 ? (
                  <div>
                    <p>
                      {processedMenus.reduce(
                        (total, menu) => total + menu.statistics.totalItems,
                        0
                      )}{" "}
                      items ready to import
                    </p>
                    <p>
                      {processedMenus.reduce(
                        (total, menu) =>
                          total + menu.statistics.categoriesFound,
                        0
                      )}{" "}
                      categories detected
                    </p>
                  </div>
                ) : (
                  <p>
                    No documents uploaded - you can add menu items manually
                    after setup.
                  </p>
                )}
              </div>

              <div className="summary-section">
                <h3>Branding</h3>
                <div className="theme-summary">
                  <div className="color-preview">
                    <div
                      className="color-swatch"
                      style={{ backgroundColor: theme.primaryColor }}
                      title="Primary Color"
                    ></div>
                    <div
                      className="color-swatch"
                      style={{ backgroundColor: theme.secondaryColor }}
                      title="Secondary Color"
                    ></div>
                    <div
                      className="color-swatch"
                      style={{ backgroundColor: theme.backgroundColor }}
                      title="Background Color"
                    ></div>
                  </div>
                  <div className="font-preview">
                    <span style={{ fontFamily: theme.titleFont }}>
                      Title Font
                    </span>
                    <span style={{ fontFamily: theme.fontFamily }}>
                      Body Font
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="restaurant-setup">
      <div className="setup-container">
        <div className="setup-header">
          <h1>Set Up Your Restaurant</h1>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(step / 4) * 100}%` }}
            ></div>
          </div>
          <div className="step-indicator">Step {step} of 4</div>
        </div>

        {error && (
          <div className="error-message">
            <span>⚠️</span>
            <p>{error}</p>
            <button onClick={() => setError("")}>×</button>
          </div>
        )}

        <div className="setup-content">{renderStep()}</div>

        <div className="setup-actions">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="btn-secondary"
              disabled={loading}
            >
              Previous
            </button>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            className="btn-primary"
            disabled={loading || !restaurantInfo.name}
          >
            {(() => {
              if (loading) return "Setting up...";
              if (step === 4) return "Launch Restaurant";
              return "Next";
            })()}
          </button>
        </div>
      </div>
    </div>
  );
};

RestaurantSetup.propTypes = {
  onComplete: PropTypes.func,
};

export default RestaurantSetup;
